import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Create axios instance with auth header
const API = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      // Optionally redirect to login
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (data) => API.post('/auth/login', data),
  logout: () => API.post('/auth/logout'),
  getMe: () => API.get('/auth/me'),
  changePassword: (data) => API.post('/auth/change-password', data),
  registerAdmin: (data) => API.post('/auth/register-admin', data),
  resetPassword: (userId, newPassword) => API.post(`/auth/reset-password/${userId}`, null, { params: { new_password: newPassword } }),
  checkAdminExists: () => API.get('/auth/check-admin-exists'),
  forgotPassword: (email) => API.post('/auth/forgot-password', { email }),
  resetPasswordWithToken: (token, newPassword) => API.post('/auth/reset-password', { token, new_password: newPassword }),
  verifyResetToken: (token) => API.get(`/auth/verify-reset-token/${token}`),
};

// Organizations API (Multi-Tenant)
export const organizationsApi = {
  getMe: () => API.get('/organizations/me'),
  update: (data) => API.put('/organizations/me', data),
  uploadLogo: (formData) => API.post('/organizations/me/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  checkSlug: (slug) => API.get(`/organizations/check-slug/${slug}`),
  getBySlug: (slug) => API.get(`/organizations/by-slug/${slug}`),
};

// Residents API
export const residentsApi = {
  getAll: (params) => API.get('/residents', { params }),
  getById: (id) => API.get(`/residents/${id}`),
  create: (data) => API.post('/residents', data),
  update: (id, data) => API.put(`/residents/${id}`, data),
  delete: (id) => API.delete(`/residents/${id}`),
  getStats: () => API.get('/residents/stats/summary'),
  // PIN Management
  generatePin: (id) => API.post(`/residents/${id}/generate-pin`),
  verifyPin: (id, pin) => API.get(`/residents/${id}/verify-pin`, { params: { pin } }),
  removePin: (id) => API.delete(`/residents/${id}/pin`),
};

// Medications API
export const medicationsApi = {
  getAll: (params) => API.get('/medications', { params }),
  getById: (id) => API.get(`/medications/${id}`),
  create: (data) => API.post('/medications', data),
  update: (id, data) => API.put(`/medications/${id}`, data),
  delete: (id, reason) => API.delete(`/medications/${id}`, { params: { reason } }),
  // eMAR
  getAdministrations: (params) => API.get('/medications/administrations/list', { params }),
  createAdministration: (data) => API.post('/medications/administrations', data),
  updateAdministration: (id, params) => API.put(`/medications/administrations/${id}`, null, { params }),
  getDailyEmar: (params) => API.get('/medications/emar/daily', { params }),
  getOverdueStats: () => API.get('/medications/stats/overdue'),
  // PIN Management
  setPin: (pin) => API.post('/medications/pin/set', null, { params: { pin } }),
  verifyPin: (pin) => API.post('/medications/pin/verify', null, { params: { pin } }),
  resetPin: (staffId) => API.post(`/medications/pin/reset/${staffId}`),
  // Alerts
  getAlerts: (params) => API.get('/medications/alerts', { params }),
  acknowledgeAlert: (alertId) => API.put(`/medications/alerts/${alertId}/acknowledge`),
  checkOverdue: () => API.get('/medications/alerts/check-overdue'),
  // Print
  getPrintData: (residentId, startDate, endDate) => API.get('/medications/emar/print', { 
    params: { resident_id: residentId, start_date: startDate, end_date: endDate } 
  }),
};

// Prescribers API
export const prescribersApi = {
  getAll: (params) => API.get('/prescribers', { params }),
  getById: (id) => API.get(`/prescribers/${id}`),
  create: (data) => API.post('/prescribers', data),
  update: (id, data) => API.put(`/prescribers/${id}`, data),
  delete: (id) => API.delete(`/prescribers/${id}`),
};

// Care Plans API
export const carePlansApi = {
  getAll: (params) => API.get('/care-plans', { params }),
  getById: (id) => API.get(`/care-plans/${id}`),
  create: (data) => API.post('/care-plans', data),
  update: (id, data) => API.put(`/care-plans/${id}`, data),
  delete: (id) => API.delete(`/care-plans/${id}`),
  generateTasks: (planId, date) => API.post(`/care-plans/${planId}/generate-tasks`, null, { params: { date } }),
  // Tasks
  getTasks: (params) => API.get('/care-plans/tasks/list', { params }),
  getTaskById: (id) => API.get(`/care-plans/tasks/${id}`),
  createTask: (data) => API.post('/care-plans/tasks', data),
  updateTask: (id, data) => API.put(`/care-plans/tasks/${id}`, data),
  getTaskStats: (params) => API.get('/care-plans/tasks/stats/daily', { params }),
  // Goals
  addGoal: (planId, goal) => API.post(`/care-plans/${planId}/goals`, goal),
  updateGoal: (planId, goalId, data) => API.put(`/care-plans/${planId}/goals/${goalId}`, data),
  deleteGoal: (planId, goalId) => API.delete(`/care-plans/${planId}/goals/${goalId}`),
  // Team Notes
  addTeamNote: (planId, note) => API.post(`/care-plans/${planId}/team-notes`, note),
  getTeamNotes: (planId, params) => API.get(`/care-plans/${planId}/team-notes`, { params }),
  // Reviews
  addReview: (planId, review) => API.post(`/care-plans/${planId}/reviews`, review),
  // Templates
  getTemplates: (params) => API.get('/care-plans/templates/list', { params }),
  getTemplateById: (id) => API.get(`/care-plans/templates/${id}`),
  createTemplate: (data) => API.post('/care-plans/templates', data),
  updateTemplate: (id, data) => API.put(`/care-plans/templates/${id}`, data),
  deleteTemplate: (id) => API.delete(`/care-plans/templates/${id}`),
  createFromTemplate: (templateId, residentId) => API.post(`/care-plans/from-template/${templateId}`, null, { params: { resident_id: residentId } }),
  seedDefaultTemplates: () => API.post('/care-plans/templates/seed-defaults'),
  // Family Access
  enableFamilyAccess: (planId) => API.post(`/care-plans/${planId}/enable-family-access`),
  disableFamilyAccess: (planId) => API.post(`/care-plans/${planId}/disable-family-access`),
  getFamilyView: (accessCode) => API.get(`/care-plans/family-view/${accessCode}`),
};

// Incidents API
export const incidentsApi = {
  getAll: (params) => API.get('/incidents', { params }),
  getById: (id) => API.get(`/incidents/${id}`),
  create: (data) => API.post('/incidents', data),
  update: (id, data) => API.put(`/incidents/${id}`, data),
  delete: (id) => API.delete(`/incidents/${id}`),
  getStats: () => API.get('/incidents/stats/summary'),
  getTrends: (days = 30) => API.get('/incidents/reports/trends', { params: { days } }),
  getStateInspectionReport: (startDate, endDate) => API.get('/incidents/reports/state-inspection', { params: { start_date: startDate, end_date: endDate } }),
  uploadPhoto: (incidentId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return API.post(`/incidents/${incidentId}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deletePhoto: (incidentId, filename) => API.delete(`/incidents/${incidentId}/photos/${filename}`),
  getPhotoUrl: (incidentId, filename) => `${BACKEND_URL}/api/incidents/${incidentId}/photos/${filename}`,
};

// Staff API
export const staffApi = {
  getAll: (params) => API.get('/staff', { params }),
  getPublic: () => API.get('/staff/public'),
  getById: (id) => API.get(`/staff/${id}`),
  create: (data) => API.post('/staff', data),
  update: (id, data) => API.put(`/staff/${id}`, data),
  delete: (id) => API.delete(`/staff/${id}`),
  getStats: () => API.get('/staff/stats/summary'),
  generateEmployeeId: (prefix = 'EMP', length = 6) => API.get('/staff/generate-employee-id', { params: { prefix, length } }),
  resetPassword: (id, newPassword) => API.post(`/staff/${id}/reset-password`, { new_password: newPassword }),
};

// Shifts API
export const shiftsApi = {
  getAll: (params) => API.get('/shifts', { params }),
  getById: (id) => API.get(`/shifts/${id}`),
  create: (data) => API.post('/shifts', data),
  createBulk: (data) => API.post('/shifts/bulk', data),
  update: (id, data) => API.put(`/shifts/${id}`, data),
  delete: (id) => API.delete(`/shifts/${id}`),
  getCalendar: (year, month) => API.get('/shifts/calendar', { params: { year, month } }),
  getMyShifts: (params) => API.get('/shifts/my-shifts', { params }),
  clockIn: (id) => API.post(`/shifts/${id}/clock-in`),
  clockOut: (id) => API.post(`/shifts/${id}/clock-out`),
  getWeeklyStats: (startDate) => API.get('/shifts/stats/weekly', { params: { start_date: startDate } }),
  getWingCoverage: (shiftDate) => API.get('/shifts/coverage/by-wing', { params: { shift_date: shiftDate } }),
  autoFill: (shiftDate, wingId, shiftType, role) => API.post('/shifts/auto-fill', null, { 
    params: { shift_date: shiftDate, wing_id: wingId, shift_type: shiftType, role } 
  }),
  getAvailableStaff: (shiftDate, shiftType, wingId, role) => API.get('/shifts/available-staff', {
    params: { shift_date: shiftDate, shift_type: shiftType, wing_id: wingId, role }
  }),
};

// Shift Swaps API
export const shiftSwapsApi = {
  getAll: (params) => API.get('/shift-swaps', { params }),
  getById: (id) => API.get(`/shift-swaps/${id}`),
  create: (data) => API.post('/shift-swaps', data),
  approve: (id) => API.put(`/shift-swaps/${id}/approve`),
  reject: (id, reason) => API.put(`/shift-swaps/${id}/reject`, { status: 'rejected', rejection_reason: reason }),
  claim: (id, exchangeShiftId) => API.put(`/shift-swaps/${id}/claim`, { exchange_shift_id: exchangeShiftId }),
  cancel: (id) => API.put(`/shift-swaps/${id}/cancel`),
  getPendingCount: () => API.get('/shift-swaps/pending-count'),
};

// Documents API
export const documentsApi = {
  getAll: (params) => API.get('/documents', { params }),
  getById: (id) => API.get(`/documents/${id}`),
  upload: (formData) => API.post('/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, data) => API.put(`/documents/${id}`, data),
  delete: (id) => API.delete(`/documents/${id}`),
  getResidentPhotos: (residentId) => API.get(`/documents/resident/${residentId}/photos`),
  getProfilePhoto: (residentId) => API.get(`/documents/resident/${residentId}/profile-photo`),
  getFileUrl: (residentId, filename) => `${BACKEND_URL}/api/documents/file/${residentId}/${filename}`,
};

// Service Orders API
export const serviceOrdersApi = {
  getAll: (params) => API.get('/service-orders', { params }),
  getById: (id) => API.get(`/service-orders/${id}`),
  getMyOrders: (params) => API.get('/service-orders/my-orders', { params }),
  getStats: () => API.get('/service-orders/stats'),
  create: (data) => API.post('/service-orders', data),
  update: (id, data) => API.put(`/service-orders/${id}`, data),
  assign: (id, staffId) => API.post(`/service-orders/${id}/assign`, null, { params: { assigned_to: staffId } }),
  start: (id) => API.post(`/service-orders/${id}/start`),
  complete: (id, data) => API.post(`/service-orders/${id}/complete`, null, { params: { actual_cost: data?.actual_cost, notes: data?.notes } }),
  cancel: (id) => API.delete(`/service-orders/${id}`),
};

// CSV Import/Export API
export const csvApi = {
  // Direct navigation download - simplest and most reliable for Safari
  downloadFile: async (type, isTemplate = false) => {
    const token = localStorage.getItem('auth_token');
    const endpoint = isTemplate 
      ? `${BACKEND_URL}/api/csv/download/template/${type}` 
      : `${BACKEND_URL}/api/csv/download/${type}`;
    
    // Direct navigation - Safari handles this reliably
    // The Content-Disposition: attachment header will trigger download without leaving the page
    window.location.href = `${endpoint}?token=${token}`;
    
    return { success: true };
  },
  
  // URL getters for direct access
  getExportUrl: (type) => {
    const token = localStorage.getItem('auth_token');
    return `${BACKEND_URL}/api/csv/download/${type}?token=${token}`;
  },
  getTemplateUrl: (type) => {
    const token = localStorage.getItem('auth_token');
    return `${BACKEND_URL}/api/csv/download/template/${type}?token=${token}`;
  },
  
  // Blob downloads (for non-Safari browsers)
  exportStaff: () => API.get('/csv/export/staff', { responseType: 'blob' }),
  exportResidents: () => API.get('/csv/export/residents', { responseType: 'blob' }),
  exportIncidents: () => API.get('/csv/export/incidents', { responseType: 'blob' }),
  getStaffTemplate: () => API.get('/csv/template/staff', { responseType: 'blob' }),
  getResidentsTemplate: () => API.get('/csv/template/residents', { responseType: 'blob' }),
  
  // Import
  importStaff: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return API.post('/csv/import/staff', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  importResidents: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return API.post('/csv/import/residents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Settings API
export const settingsApi = {
  get: () => API.get('/settings'),
  update: (data) => API.put('/settings', data),
  getPublic: () => API.get('/settings/public'),
};

// Permissions API
export const permissionsApi = {
  getAll: () => API.get('/permissions'),
  getModules: () => API.get('/permissions/modules'),
  getRoles: () => API.get('/permissions/roles'),
  getRolePermissions: (role) => API.get(`/permissions/${role}`),
  updateRolePermissions: (role, permissions) => API.put(`/permissions/${role}`, { permissions }),
  resetRole: (role) => API.post(`/permissions/reset/${role}`),
  resetAll: () => API.post('/permissions/reset-all'),
  syncAll: () => API.post('/permissions/sync-all'),
  checkPermission: (module, action) => API.get(`/permissions/check/${module}/${action}`),
};

// Resident Accounting API
export const residentAccountingApi = {
  // Accounts
  getAccounts: (params) => API.get('/resident-accounting/accounts', { params }),
  getAccount: (residentId) => API.get(`/resident-accounting/accounts/${residentId}`),
  updateAccount: (residentId, data) => API.put(`/resident-accounting/accounts/${residentId}`, data),
  
  // Transactions
  getTransactions: (params) => API.get('/resident-accounting/transactions', { params }),
  getTransaction: (id) => API.get(`/resident-accounting/transactions/${id}`),
  createTransaction: (data) => API.post('/resident-accounting/transactions', data),
  voidTransaction: (id, reason) => API.put(`/resident-accounting/transactions/${id}/void`, null, { params: { void_reason: reason } }),
  
  // Trust Fund
  createTrustFundTransaction: (data) => API.post('/resident-accounting/trust-fund/transaction', data),
  
  // Billing - Manual
  createManualBill: (data) => API.post('/resident-accounting/billing/manual', null, { params: data }),
  
  // Billing - Recurring
  getRecurringBills: (params) => API.get('/resident-accounting/billing/recurring', { params }),
  createRecurringBill: (data) => API.post('/resident-accounting/billing/recurring', data),
  updateRecurringBill: (id, data) => API.put(`/resident-accounting/billing/recurring/${id}`, null, { params: data }),
  deleteRecurringBill: (id) => API.delete(`/resident-accounting/billing/recurring/${id}`),
  processRecurringBills: () => API.post('/resident-accounting/billing/process-recurring'),
  
  // Billing History
  getBillingHistory: (residentId, params) => API.get(`/resident-accounting/billing/history/${residentId}`, { params }),
  
  // Reports
  getStatement: (residentId, startDate, endDate) => API.get(`/resident-accounting/reports/statement/${residentId}`, { 
    params: { start_date: startDate, end_date: endDate } 
  }),
  getSummary: () => API.get('/resident-accounting/reports/summary'),
  getLowBalanceAccounts: (threshold) => API.get('/resident-accounting/reports/low-balance', { params: { threshold } }),
};

// Billing Automation API
export const billingAutomationApi = {
  // Configuration
  getConfig: () => API.get('/billing-automation/config'),
  updateConfig: (data) => API.put('/billing-automation/config', data),
  
  // Scheduler
  getSchedulerStatus: () => API.get('/billing-automation/scheduler/status'),
  startScheduler: () => API.post('/billing-automation/scheduler/start'),
  stopScheduler: () => API.post('/billing-automation/scheduler/stop'),
  
  // Manual Job Triggers
  runAllJobs: () => API.post('/billing-automation/jobs/run-all'),
  processRecurring: () => API.post('/billing-automation/jobs/process-recurring'),
  generateInvoices: (periodMonth) => API.post('/billing-automation/jobs/generate-invoices', null, { params: { period_month: periodMonth } }),
  sendLowBalanceAlerts: () => API.post('/billing-automation/jobs/low-balance-alerts'),
  sendOverdueReminders: () => API.post('/billing-automation/jobs/overdue-reminders'),
  
  // Invoices
  getInvoices: (params) => API.get('/billing-automation/invoices', { params }),
  getInvoice: (id) => API.get(`/billing-automation/invoices/${id}`),
  createInvoice: (data) => API.post('/billing-automation/invoices', data),
  updateInvoiceStatus: (id, status) => API.put(`/billing-automation/invoices/${id}/status`, null, { params: { status } }),
  sendInvoiceEmail: (id, email) => API.post(`/billing-automation/invoices/${id}/send-email`, null, { params: { recipient_email: email } }),
  
  // Notifications
  getNotifications: (params) => API.get('/billing-automation/notifications', { params }),
  
  // Job Logs
  getJobLogs: (params) => API.get('/billing-automation/job-logs', { params }),
  
  // Dashboard
  getDashboard: () => API.get('/billing-automation/dashboard'),
  
  // Care Level Billing
  getCareLevelSummary: () => API.get('/billing-automation/care-levels/summary'),
  updateCareLevelRates: (rates) => API.put('/billing-automation/care-levels/rates', rates),
  getResidentsWithCareLevels: (careLevel) => API.get('/billing-automation/residents/care-levels', { params: { care_level: careLevel } }),
};

// Payments API
export const paymentsApi = {
  // Pay invoice
  payInvoice: (invoiceId, originUrl) => API.post('/payments/invoice/pay', { invoice_id: invoiceId, origin_url: originUrl }),
  
  // Make deposit
  makeDeposit: (residentId, amount, originUrl, description) => API.post('/payments/deposit', { 
    resident_id: residentId, 
    amount: amount, 
    origin_url: originUrl,
    description: description || 'Account Deposit'
  }),
  
  // Check payment status
  getPaymentStatus: (sessionId) => API.get(`/payments/status/${sessionId}`),
  
  // Payment history
  getPaymentHistory: (params) => API.get('/payments/history', { params }),
};

// Wings API
export const wingsApi = {
  getAll: (includeInactive = false) => API.get('/wings', { params: { include_inactive: includeInactive } }),
  getById: (id) => API.get(`/wings/${id}`),
  create: (data) => API.post('/wings', data),
  update: (id, data) => API.put(`/wings/${id}`, data),
  delete: (id) => API.delete(`/wings/${id}`),
  getResidents: (id) => API.get(`/wings/${id}/residents`),
  getStaff: (id) => API.get(`/wings/${id}/staff`),
  getAvailableBeds: (id) => API.get(`/wings/${id}/available-beds`),
  getAllAvailableBeds: () => API.get('/wings/all/available-beds'),
};

// Staffing Requirements API
export const staffingRequirementsApi = {
  getAll: () => API.get('/staffing-requirements'),
  getByWing: (wingId) => API.get(`/staffing-requirements/wing/${wingId}`),
  setByWing: (wingId, data) => API.put(`/staffing-requirements/wing/${wingId}`, data),
  deleteByWing: (wingId) => API.delete(`/staffing-requirements/wing/${wingId}`),
  getCoverageStatus: (shiftDate) => API.get('/staffing-requirements/coverage/status', { params: { shift_date: shiftDate } }),
};

// Dashboard API
export const dashboardApi = {
  getStats: (wingId = null) => {
    const params = wingId ? { wing_id: wingId } : {};
    return API.get('/dashboard/stats', { params });
  },
  getHealth: () => API.get('/health'),
};

// Applications API
export const applicationsApi = {
  // Public endpoints (no auth)
  getPositions: () => axios.get(`${BACKEND_URL}/api/applications/positions/available`),
  submit: (data) => axios.post(`${BACKEND_URL}/api/applications`, data),
  submitWithResume: (formData) => axios.post(`${BACKEND_URL}/api/applications/with-resume`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // Protected endpoints (require auth)
  getAll: (params) => API.get('/applications', { params }),
  getStats: () => API.get('/applications/stats'),
  getById: (id) => API.get(`/applications/${id}`),
  update: (id, data) => API.put(`/applications/${id}`, data),
  delete: (id) => API.delete(`/applications/${id}`),
  onboard: (id, data) => API.post(`/applications/${id}/onboard`, data),
};

// ICD-10 API
export const icd10Api = {
  search: (query, category = null, limit = 50) => API.get('/icd10/search', { 
    params: { q: query, category, limit } 
  }),
  getCategories: () => API.get('/icd10/categories'),
  getByCategory: (category) => API.get(`/icd10/category/${category}`),
  getCode: (code) => API.get(`/icd10/code/${code}`),
  validate: (code) => API.post('/icd10/validate', null, { params: { code } }),
  getCommonCodes: () => API.get('/icd10/common'),
};

// Communications API
export const communicationsApi = {
  getAll: (params) => API.get('/communications', { params }),
  getById: (id) => API.get(`/communications/${id}`),
  getUnreadCount: () => API.get('/communications/unread-count'),
  getMyMentions: () => API.get('/communications/mentions/me'),
  send: (data) => API.post('/communications', data),
  reply: (id, data) => API.post(`/communications/${id}/reply`, data),
  togglePin: (id) => API.put(`/communications/${id}/pin`),
  delete: (id) => API.delete(`/communications/${id}`),
  getRecipients: () => API.get('/communications/recipients/available'),
  getRoles: () => API.get('/communications/roles/available'),
};

// HR Templates API
export const hrTemplatesApi = {
  // Templates
  getTemplates: (params) => API.get('/hr-templates/templates', { params }),
  getTemplate: (id) => API.get(`/hr-templates/templates/${id}`),
  getTemplateTypes: () => API.get('/hr-templates/templates/types'),
  getTemplateVariables: () => API.get('/hr-templates/templates/variables'),
  createTemplate: (data) => API.post('/hr-templates/templates', data),
  updateTemplate: (id, data) => API.put(`/hr-templates/templates/${id}`, data),
  deleteTemplate: (id) => API.delete(`/hr-templates/templates/${id}`),
  initializeDefaults: () => API.post('/hr-templates/templates/initialize-defaults'),
  
  // Documents
  getDocuments: (params) => API.get('/hr-templates/documents', { params }),
  getDocument: (id) => API.get(`/hr-templates/documents/${id}`),
  getStaffDocuments: (staffId) => API.get(`/hr-templates/documents/staff/${staffId}`),
  createDocument: (data) => API.post('/hr-templates/documents', data),
  updateDocument: (id, data) => API.put(`/hr-templates/documents/${id}`, data),
  deleteDocument: (id) => API.delete(`/hr-templates/documents/${id}`),
  finalizeDocument: (id) => API.post(`/hr-templates/documents/${id}/finalize`),
  previewDocument: (id) => API.get(`/hr-templates/documents/${id}/preview`),
};

// HR Management API
export const hrManagementApi = {
  getPermissions: () => API.get('/auth/hr/permissions'),
  resetPassword: (data) => API.post('/auth/hr/reset-password', data),
  toggleAccount: (data) => API.post('/auth/hr/toggle-account', data),
  getStaffAccounts: (includeDisabled = false) => API.get('/auth/hr/staff-accounts', { params: { include_disabled: includeDisabled } }),
};

// Charting API (formerly CNA Charting)
export const cnaChartingApi = {
  // Assignment & shift info
  getCurrentShift: () => API.get('/cna-charting/current-shift'),
  getMyAssignment: (wingFilter) => API.get('/cna-charting/my-assignment', { params: wingFilter ? { wing_filter: wingFilter } : {} }),
  
  // Chart entries
  createEntry: (data) => API.post('/cna-charting', data),
  getResidentCharts: (residentId, params) => API.get(`/cna-charting/resident/${residentId}`, { params }),
  getLastCharted: (residentId, chartType) => API.get(`/cna-charting/resident/${residentId}/last-charted`, { params: { chart_type: chartType } }),
  deleteEntry: (id) => API.delete(`/cna-charting/${id}`),
  
  // Shift summary
  getShiftSummary: (params) => API.get('/cna-charting/shift-summary', { params }),
  
  // Nurse alerts
  getNurseAlerts: (params) => API.get('/cna-charting/nurse-alerts', { params }),
  acknowledgeAlert: (id) => API.put(`/cna-charting/nurse-alerts/${id}/acknowledge`),
  
  // Reference data
  getAssistanceLevels: () => API.get('/cna-charting/assistance-levels'),
  getBehaviorTypes: () => API.get('/cna-charting/behavior-types'),
  getSkinObservationTypes: () => API.get('/cna-charting/skin-observation-types'),
  getInterventionTypes: () => API.get('/cna-charting/intervention-types'),
  
  // Analytics
  getChartingAnalytics: (params) => API.get('/cna-charting/analytics/overview', { params }),
  getCNADetailAnalytics: (cnaId, params) => API.get(`/cna-charting/analytics/cna/${cnaId}`, { params }),
};

// Time Clock API
export const timeClockApi = {
  // Clock actions
  getStatus: () => API.get('/time-clock/status'),
  clockIn: (data = {}) => API.post('/time-clock/clock-in', data),
  clockOut: (data = {}) => API.post('/time-clock/clock-out', data),
  startBreak: (data = {}) => API.post('/time-clock/start-break', data),
  endBreak: (data = {}) => API.post('/time-clock/end-break', data),
  
  // Entries
  getEntries: (params) => API.get('/time-clock/entries', { params }),
  getEntry: (id) => API.get(`/time-clock/entries/${id}`),
  updateEntry: (id, data) => API.put(`/time-clock/entries/${id}`, data),
  deleteEntry: (id) => API.delete(`/time-clock/entries/${id}`),
  
  // Approvals
  approveEntries: (data) => API.post('/time-clock/approve', data),
  getPendingApprovals: () => API.get('/time-clock/pending-approvals'),
  
  // Summaries
  getWeeklySummary: (params) => API.get('/time-clock/weekly-summary', { params }),
  getTodaySummary: () => API.get('/time-clock/today-summary'),
  getOvertimeSettings: () => API.get('/time-clock/overtime-settings'),
  
  // Payroll export
  exportToPayroll: (params) => API.get('/time-clock/payroll-export', { params }),
  
  // QR Code functions
  getQRCodeData: (staffId) => API.get(`/time-clock/qr-code-data/${staffId}`),
  getQRCodeImage: (staffId) => API.get(`/time-clock/qr-code/${staffId}`, { responseType: 'blob' }),
  getAllStaffQRCodes: () => API.get('/time-clock/all-staff-qr-codes'),
  processQRPunch: (qrCode) => API.post('/time-clock/qr-punch', null, { params: { qr_code: qrCode } }),
  
  // Location QR Codes
  getLocationQR: (locationName) => API.get(`/time-clock/location-qr/${encodeURIComponent(locationName)}`),
};

// Dietary API
export const dietaryApi = {
  // Dashboard
  getDashboard: (date) => API.get('/dietary/dashboard', { params: { date } }),
  
  // Staff
  getStaff: (params) => API.get('/dietary/staff', { params }),
  createStaff: (data) => API.post('/dietary/staff', data),
  updateStaff: (id, data) => API.put(`/dietary/staff/${id}`, data),
  deleteStaff: (id) => API.delete(`/dietary/staff/${id}`),
  
  // Resident Diet Profiles
  getResidentDiets: (params) => API.get('/dietary/residents/diets', { params }),
  getResidentDiet: (residentId) => API.get(`/dietary/residents/${residentId}/diet`),
  updateResidentDiet: (residentId, data) => API.post(`/dietary/residents/${residentId}/diet`, data),
  
  // Menu Items
  getMenuItems: (params) => API.get('/dietary/menu-items', { params }),
  createMenuItem: (data) => API.post('/dietary/menu-items', data),
  deleteMenuItem: (id) => API.delete(`/dietary/menu-items/${id}`),
  
  // Weekly Menus
  getWeeklyMenus: (params) => API.get('/dietary/menus/weekly', { params }),
  createWeeklyMenu: (data) => API.post('/dietary/menus/weekly', data),
  getWeeklyMenu: (id) => API.get(`/dietary/menus/weekly/${id}`),
  activateWeeklyMenu: (id) => API.put(`/dietary/menus/weekly/${id}/activate`),
  bulkUpdateDailyMenus: (menuId, dailyMenus) => API.put(`/dietary/menus/weekly/${menuId}/bulk-daily`, dailyMenus),
  copyWeeklyMenu: (menuId, targetStartDate, newName) => API.post(`/dietary/menus/weekly/${menuId}/copy`, null, { 
    params: { target_start_date: targetStartDate, new_name: newName } 
  }),
  applyTemplate: (menuId, templateId) => API.post(`/dietary/menus/weekly/${menuId}/apply-template`, null, { 
    params: { template_id: templateId } 
  }),
  
  // Daily Menus
  getDailyMenu: (date) => API.get('/dietary/menus/daily', { params: { menu_date: date } }),
  createOrUpdateDailyMenu: (data, weekId) => API.post('/dietary/menus/daily', data, { params: { week_id: weekId } }),
  
  // Menu Templates
  getMenuTemplates: () => API.get('/dietary/menu-templates'),
  saveMenuTemplate: (data) => API.post('/dietary/menu-templates', data),
  deleteMenuTemplate: (id) => API.delete(`/dietary/menu-templates/${id}`),
  
  // Meal Intake
  getMealIntake: (params) => API.get('/dietary/meal-intake', { params }),
  recordMealIntake: (data) => API.post('/dietary/meal-intake', data),
  getMealIntakeSummary: (date) => API.get('/dietary/meal-intake/summary', { params: { meal_date: date } }),
  
  // Diet Census
  getDietCensus: () => API.get('/dietary/census'),
  
  // Tray Cards
  getTrayCards: (wingId) => API.get('/dietary/tray-cards', { params: wingId ? { wing_id: wingId } : {} }),
  
  // Tray Service
  getTrayService: (date, mealType, wingId) => API.get(`/dietary/tray-service/${date}/${mealType}`, { 
    params: wingId ? { wing_id: wingId } : {} 
  }),
  serveTray: (residentId, date, mealType, served, notes) => API.post('/dietary/tray-service/serve', null, { 
    params: { resident_id: residentId, date, meal_type: mealType, served, notes } 
  }),
  serveTrays: (residentIds, date, mealType) => API.post('/dietary/tray-service/serve-bulk', residentIds, { 
    params: { date, meal_type: mealType } 
  }),
  
  // Special Requests
  getSpecialRequests: (params) => API.get('/dietary/special-requests', { params }),
  createSpecialRequest: (data) => API.post('/dietary/special-requests', data),
  updateSpecialRequestStatus: (id, status) => API.put(`/dietary/special-requests/${id}/status`, null, { params: { status } }),
  
  // Meal Production
  getDailyProduction: (date) => API.get(`/dietary/meal-production/${date}`),
  getMealProduction: (date, mealType) => API.get(`/dietary/meal-production/${date}/${mealType}`),
  
  // Schedule
  getSchedule: (startDate, endDate) => API.get('/dietary/schedule', { params: { start_date: startDate, end_date: endDate } }),
  createSchedule: (data) => API.post('/dietary/schedule', null, { params: data }),
  deleteSchedule: (id) => API.delete(`/dietary/schedule/${id}`),
  
  // Reports
  getDailyTrayReport: (date) => API.get('/dietary/reports/daily-tray-count', { params: { date } }),
  getAllergyReport: () => API.get('/dietary/reports/allergy-list'),
  getProductionSummary: (startDate, endDate) => API.get('/dietary/reports/production-summary', { 
    params: { start_date: startDate, end_date: endDate } 
  }),
  getWeeklyMenuPrint: (startDate) => API.get('/dietary/reports/weekly-menu-print', { params: { start_date: startDate } }),
};

// Payroll API
export const payrollApi = {
  // Dashboard
  getDashboard: () => API.get('/payroll/dashboard'),
  
  // Timesheets
  getTimesheets: (params) => API.get('/payroll/timesheets', { params }),
  getTimesheet: (id) => API.get(`/payroll/timesheets/${id}`),
  createTimesheet: (data) => API.post('/payroll/timesheets', data),
  updateTimesheet: (id, data) => API.put(`/payroll/timesheets/${id}`, data),
  submitTimesheet: (id) => API.post(`/payroll/timesheets/${id}/submit`),
  approveTimesheet: (id) => API.put(`/payroll/timesheets/${id}`, { status: 'approved' }),
  rejectTimesheet: (id) => API.put(`/payroll/timesheets/${id}`, { status: 'rejected' }),
  
  // Import
  importFromShifts: (startDate, endDate) => API.post(`/payroll/timesheets/import-from-shifts?pay_period_start=${startDate}&pay_period_end=${endDate}`),
  importFromTimeClock: (startDate, endDate) => API.post(`/payroll/timesheets/import-from-timeclock?pay_period_start=${startDate}&pay_period_end=${endDate}`),
  
  // Payroll runs
  getPayrollRuns: (params) => API.get('/payroll/runs', { params }),
  createPayrollRun: (data) => API.post('/payroll/runs', data),
  approvePayrollRun: (id) => API.post(`/payroll/runs/${id}/approve`),
  processPayrollRun: (id) => API.post(`/payroll/runs/${id}/process`),
  
  // Pay stubs
  getPayStubs: (params) => API.get('/payroll/pay-stubs', { params }),
  
  // Config
  getConfig: () => API.get('/payroll/config'),
  updateConfig: (data) => API.put('/payroll/config', data),
};

// Caregiver Care Plan API
export const caregiverCarePlanApi = {
  // ISP Structured Items
  getISPItems: (residentId, category) => API.get(`/caregiver-care-plans/isp-items/${residentId}`, { params: category ? { category } : {} }),
  createISPItem: (residentId, data) => API.post(`/caregiver-care-plans/isp-items/${residentId}`, data),
  updateISPItem: (residentId, itemId, data) => API.put(`/caregiver-care-plans/isp-items/${residentId}/${itemId}`, data),
  deleteISPItem: (residentId, itemId) => API.delete(`/caregiver-care-plans/isp-items/${residentId}/${itemId}`),
  
  // Mappings
  getMappings: () => API.get('/caregiver-care-plans/mappings'),
  updateMapping: (mappingId, data) => API.put(`/caregiver-care-plans/mappings/${mappingId}`, data),
  
  // Care Plans
  getCarePlan: (residentId) => API.get(`/caregiver-care-plans/${residentId}`),
  generateCarePlan: (residentId) => API.post(`/caregiver-care-plans/${residentId}/generate`),
  syncCarePlan: (residentId) => API.post(`/caregiver-care-plans/${residentId}/sync`),
  updateSection: (residentId, sectionId, data) => API.put(`/caregiver-care-plans/${residentId}/sections/${sectionId}`, data),
  signCarePlan: (residentId, data) => API.post(`/caregiver-care-plans/${residentId}/sign`, data),
  listCarePlans: (params) => API.get('/caregiver-care-plans', { params }),
  
  // Phase 2: Review workflow
  getAlerts: () => API.get('/caregiver-care-plans/alerts'),
  acknowledgeSection: (residentId, sectionId, notes) => API.post(`/caregiver-care-plans/${residentId}/sections/${sectionId}/acknowledge`, null, { params: notes ? { notes } : {} }),
  completeReview: (residentId, notes) => API.post(`/caregiver-care-plans/${residentId}/complete-review`, null, { params: notes ? { notes } : {} }),
  setReviewDate: (residentId, sectionId, reviewDate) => API.put(`/caregiver-care-plans/${residentId}/sections/${sectionId}/set-review-date`, null, { params: { review_date: reviewDate } }),
  
  // Phase 3: Audit & Dashboard
  getAuditLog: (residentId, limit) => API.get(`/caregiver-care-plans/${residentId}/audit-log`, { params: { limit: limit || 50 } }),
  getDashboardStats: () => API.get('/caregiver-care-plans/dashboard/stats'),
  getResidentsNeedingPlans: () => API.get('/caregiver-care-plans/dashboard/residents-needing-plans'),
  
  // Reference Data
  getCategories: () => API.get('/caregiver-care-plans/reference/categories'),
  getDomains: () => API.get('/caregiver-care-plans/reference/domains'),
};

// DHS 83 Compliance API
export const dhs83Api = {
  // Comprehensive Assessments (§83.35(1))
  getAssessments: (params) => API.get('/dhs83/assessments', { params }),
  getAssessment: (id) => API.get(`/dhs83/assessments/${id}`),
  getLatestAssessment: (residentId) => API.get(`/dhs83/assessments/resident/${residentId}/latest`),
  createAssessment: (data) => API.post('/dhs83/assessments', data),
  updateAssessment: (id, data) => API.put(`/dhs83/assessments/${id}`, data),
  deleteAssessment: (id) => API.delete(`/dhs83/assessments/${id}`),
  getAssessmentStats: () => API.get('/dhs83/assessments/stats'),
  
  // Temporary Service Plans (§83.35(2))
  getTempPlans: (params) => API.get('/dhs83/temp-plans', { params }),
  getTempPlan: (id) => API.get(`/dhs83/temp-plans/${id}`),
  createTempPlan: (data) => API.post('/dhs83/temp-plans', data),
  
  // Individual Service Plans (§83.35(3))
  getISPs: (params) => API.get('/dhs83/isp', { params }),
  getISP: (id) => API.get(`/dhs83/isp/${id}`),
  getCurrentISP: (residentId) => API.get(`/dhs83/isp/resident/${residentId}/current`),
  createISP: (data) => API.post('/dhs83/isp', data),
  updateISP: (id, data) => API.put(`/dhs83/isp/${id}`, data),
  addISPSignature: (ispId, signature) => API.post(`/dhs83/isp/${ispId}/sign`, signature),
  getISPStats: () => API.get('/dhs83/isp/stats'),
  
  // Satisfaction Evaluations (§83.35(4))
  getSatisfactionEvals: (params) => API.get('/dhs83/satisfaction', { params }),
  getSatisfactionEval: (id) => API.get(`/dhs83/satisfaction/${id}`),
  createSatisfactionEval: (data) => API.post('/dhs83/satisfaction', data),
  updateSatisfactionEval: (id, data) => API.put(`/dhs83/satisfaction/${id}`, data),
  getSatisfactionStats: () => API.get('/dhs83/satisfaction/stats'),
  
  // Evacuation Assessments (§83.35(5))
  getEvacuationAssessments: (params) => API.get('/dhs83/evacuation', { params }),
  getEvacuationAssessment: (id) => API.get(`/dhs83/evacuation/${id}`),
  getCurrentEvacuation: (residentId) => API.get(`/dhs83/evacuation/resident/${residentId}/current`),
  createEvacuationAssessment: (data) => API.post('/dhs83/evacuation', data),
  updateEvacuationAssessment: (id, data) => API.put(`/dhs83/evacuation/${id}`, data),
  notifyStaffEvacuation: (assessmentId) => API.post(`/dhs83/evacuation/${assessmentId}/notify-staff`),
  getEvacuationStats: () => API.get('/dhs83/evacuation/stats'),
  
  // Compliance Dashboard
  getResidentCompliance: (residentId) => API.get(`/dhs83/compliance/resident/${residentId}`),
  getFacilityCompliance: () => API.get('/dhs83/compliance/facility'),
  getComplianceAlerts: () => API.get('/dhs83/compliance/alerts'),
};

// Compliance Reminders API
export const complianceRemindersApi = {
  getAll: (daysAhead = 30) => API.get('/compliance-reminders', { params: { days_ahead: daysAhead } }),
  getSummary: () => API.get('/compliance-reminders/summary'),
  getTraining: (daysAhead = 30) => API.get('/compliance-reminders/training', { params: { days_ahead: daysAhead } }),
  getLicenses: (daysAhead = 30) => API.get('/compliance-reminders/licenses', { params: { days_ahead: daysAhead } }),
  getAssessments: (daysAhead = 30) => API.get('/compliance-reminders/assessments', { params: { days_ahead: daysAhead } }),
  getStaffReminders: (staffId) => API.get(`/compliance-reminders/staff/${staffId}`),
  getResidentReminders: (residentId) => API.get(`/compliance-reminders/resident/${residentId}`),
  triggerReminders: () => API.post('/compliance-reminders/send-reminders'),
};

// PTO Request API
export const ptoApi = {
  getAll: (params) => API.get('/pto', { params }),
  getPending: () => API.get('/pto/pending'),
  getMyRequests: () => API.get('/pto/my-requests'),
  getStats: () => API.get('/pto/stats'),
  getById: (id) => API.get(`/pto/${id}`),
  create: (data) => API.post('/pto', data),
  update: (id, data) => API.put(`/pto/${id}`, data),
  approve: (id, data) => API.post(`/pto/${id}/approve`, data),
  cancel: (id) => API.post(`/pto/${id}/cancel`),
  delete: (id) => API.delete(`/pto/${id}`),
  getAvailability: (staffId) => API.get(`/pto/availability/${staffId}`),
  updateAvailability: (staffId, data) => API.put(`/pto/availability/${staffId}`, data),
};

// Advanced Scheduling API
export const schedulingApi = {
  getOvertimeWarnings: (weekStart) => API.get('/scheduling/overtime-warnings', { params: { week_start: weekStart } }),
  checkOvertime: (staffId, date, hours = 8) => API.get('/scheduling/check-overtime', { params: { staff_id: staffId, date, hours } }),
  getSuggestions: (date, shiftType = 'day', wingId) => API.get('/scheduling/suggestions', { params: { date, shift_type: shiftType, wing_id: wingId } }),
  getCoverage: (date) => API.get('/scheduling/coverage', { params: { date } }),
  getStaffHours: (staffId, weekStart) => API.get(`/scheduling/staff-hours/${staffId}`, { params: { week_start: weekStart } }),
  getWeekSummary: (weekStart) => API.get('/scheduling/week-summary', { params: { week_start: weekStart } }),
};

// Reports API
export const reportsApi = {
  getTemplates: () => API.get('/reports/templates'),
  getTemplate: (id) => API.get(`/reports/templates/${id}`),
  getSummary: () => API.get('/reports/summary'),
  generate: (reportType, params) => API.post('/reports/generate', null, { params: { report_type: reportType, ...params } }),
  getIncidentReport: (params) => API.get('/reports/incident', { params }),
  getStaffingReport: (params) => API.get('/reports/staffing', { params }),
  getComplianceReport: () => API.get('/reports/compliance'),
  getMedicationReport: (params) => API.get('/reports/medication', { params }),
  getTrainingReport: (params) => API.get('/reports/training', { params }),
  getCensusReport: (params) => API.get('/reports/census', { params }),
  exportReport: (reportType, format, params) => API.get(`/reports/export/${reportType}`, { 
    params: { format, ...params },
    responseType: format === 'csv' ? 'blob' : 'json'
  }),
};

// CLTS Case Management API
export const cltsApi = {
  // Participants
  getParticipants: (params) => API.get('/clts/participants', { params }),
  getParticipant: (id) => API.get(`/clts/participants/${id}`),
  createParticipant: (data) => API.post('/clts/participants', data),
  updateParticipant: (id, data) => API.put(`/clts/participants/${id}`, data),
  deleteParticipant: (id) => API.delete(`/clts/participants/${id}`),
  
  // Profile Photos
  uploadPhoto: (participantId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return API.post(`/clts/participants/${participantId}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getPhotoUrl: (participantId) => `${API.defaults.baseURL}/clts/participants/${participantId}/photo`,
  getPhoto: (participantId) => API.get(`/clts/participants/${participantId}/photo`, { responseType: 'blob' }),
  deletePhoto: (participantId) => API.delete(`/clts/participants/${participantId}/photo`),
  
  // Photo Gallery
  getGallery: (participantId) => API.get(`/clts/participants/${participantId}/gallery`),
  uploadGalleryPhoto: (participantId, file, caption) => {
    const formData = new FormData();
    formData.append('file', file);
    if (caption) formData.append('caption', caption);
    return API.post(`/clts/participants/${participantId}/gallery`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadGalleryPhotosBulk: (participantId, files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return API.post(`/clts/participants/${participantId}/gallery/bulk`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getGalleryPhotoUrl: (participantId, photoId) => `${API.defaults.baseURL}/clts/participants/${participantId}/gallery/${photoId}`,
  updateGalleryPhoto: (participantId, photoId, caption) => API.put(`/clts/participants/${participantId}/gallery/${photoId}`, null, { params: { caption } }),
  deleteGalleryPhoto: (participantId, photoId) => API.delete(`/clts/participants/${participantId}/gallery/${photoId}`),
  
  // Contacts
  getContacts: (params) => API.get('/clts/contacts', { params }),
  getContact: (id) => API.get(`/clts/contacts/${id}`),
  createContact: (data) => API.post('/clts/contacts', data),
  updateContact: (id, data) => API.put(`/clts/contacts/${id}`, data),
  deleteContact: (id) => API.delete(`/clts/contacts/${id}`),
  getParticipantContacts: (participantId) => API.get(`/clts/participants/${participantId}/contacts`),
  
  // Dashboard
  getStats: () => API.get('/clts/dashboard/stats'),
  getAlerts: (limit = 20) => API.get('/clts/dashboard/alerts', { params: { limit } }),
  
  // Organization Profile
  getOrganization: () => API.get('/clts/organization'),
  updateOrganization: (data) => API.put('/clts/organization', data),
  
  // Assessments
  addAssessment: (participantId, data) => API.post(`/clts/participants/${participantId}/assessments`, data),
  
  // Authorizations (from billing)
  getAuthorizations: (params) => API.get('/billing/authorizations', { params }),
  
  // Schedule Substitutions
  getSubstitutions: (params) => API.get('/clts/schedules/substitutions', { params }),
  createSubstitution: (data) => API.post('/clts/schedules/substitutions', data),
  deleteSubstitution: (id) => API.delete(`/clts/schedules/substitutions/${id}`),
};

// CLTS Forms API
export const cltsFormsApi = {
  // Visit Documentation
  getVisits: (params) => API.get('/clts/forms/visits', { params }),
  getVisit: (id) => API.get(`/clts/forms/visits/${id}`),
  createVisit: (data) => API.post('/clts/forms/visits', data),
  updateVisit: (id, data) => API.put(`/clts/forms/visits/${id}`, data),
  deleteVisit: (id) => API.delete(`/clts/forms/visits/${id}`),
  getMyVisitsToday: (localDate) => API.get('/clts/forms/visits/my/today', { params: { local_date: localDate } }),
  checkInVisit: (id) => API.post(`/clts/forms/visits/${id}/check-in`),
  checkOutVisit: (id) => API.post(`/clts/forms/visits/${id}/check-out`),
  
  // Intake Forms
  getIntakeForms: (params) => API.get('/clts/forms/intake', { params }),
  getIntakeForm: (id) => API.get(`/clts/forms/intake/${id}`),
  getParticipantIntake: (participantId) => API.get(`/clts/forms/intake/participant/${participantId}`),
  createIntakeForm: (data) => API.post('/clts/forms/intake', data),
  updateIntakeForm: (id, data) => API.put(`/clts/forms/intake/${id}`, data),
  deleteIntakeForm: (id) => API.delete(`/clts/forms/intake/${id}`),
  
  // Respite Forms
  getRespiteForms: (params) => API.get('/clts/forms/respite', { params }),
  getRespiteForm: (id) => API.get(`/clts/forms/respite/${id}`),
  getParticipantRespiteForms: (participantId) => API.get(`/clts/forms/respite/participant/${participantId}`),
  createRespiteForm: (data) => API.post('/clts/forms/respite', data),
  updateRespiteForm: (id, data) => API.put(`/clts/forms/respite/${id}`, data),
  deleteRespiteForm: (id) => API.delete(`/clts/forms/respite/${id}`),
  
  // Transportation Logs
  getTransportationLogs: (params) => API.get('/clts/forms/transportation-logs', { params }),
  getTransportationLog: (id) => API.get(`/clts/forms/transportation-logs/${id}`),
  createTransportationLog: (data) => API.post('/clts/forms/transportation-logs', data),
  updateTransportationLog: (id, data) => API.put(`/clts/forms/transportation-logs/${id}`, data),
  deleteTransportationLog: (id) => API.delete(`/clts/forms/transportation-logs/${id}`),
  addTrip: (logId, trip) => API.post(`/clts/forms/transportation-logs/${logId}/trips`, trip),
  removeTrip: (logId, tripId) => API.delete(`/clts/forms/transportation-logs/${logId}/trips/${tripId}`),
  submitTransportationLog: (id) => API.post(`/clts/forms/transportation-logs/${id}/submit`),
  approveTransportationLog: (id, signature) => API.post(`/clts/forms/transportation-logs/${id}/approve`, signature),
  
  // Mentoring Visits
  getMentoringVisits: (params) => API.get('/clts/forms/mentoring-visits', { params }),
  getMentoringVisit: (id) => API.get(`/clts/forms/mentoring-visits/${id}`),
  createMentoringVisit: (data) => API.post('/clts/forms/mentoring-visits', data),
  updateMentoringVisit: (id, data) => API.put(`/clts/forms/mentoring-visits/${id}`, data),
  deleteMentoringVisit: (id) => API.delete(`/clts/forms/mentoring-visits/${id}`),
  submitMentoringVisit: (id) => API.post(`/clts/forms/mentoring-visits/${id}/submit`),
  approveMentoringVisit: (id, initials) => API.post(`/clts/forms/mentoring-visits/${id}/approve?initials=${initials}`),
  
  // Service-based form selection
  getParticipantServiceForms: (participantId) => API.get(`/clts/forms/participant/${participantId}/service-forms`),
  
  // Stats
  getFormsStats: () => API.get('/clts/forms/stats'),
};

// CLTS ISP (Individual Service Plan) API
export const cltsIspApi = {
  // ISP CRUD
  getISPs: (params) => API.get('/clts/isp', { params }),
  getISP: (id) => API.get(`/clts/isp/${id}`),
  createISP: (data) => API.post('/clts/isp', data),
  updateISP: (id, data) => API.put(`/clts/isp/${id}`, data),
  deleteISP: (id) => API.delete(`/clts/isp/${id}`),
  
  // Goals
  addGoal: (ispId, goal) => API.post(`/clts/isp/${ispId}/goals`, goal),
  updateGoal: (ispId, goalId, data) => API.put(`/clts/isp/${ispId}/goals/${goalId}`, data),
  addGoalProgress: (ispId, goalId, progress) => API.post(`/clts/isp/${ispId}/goals/${goalId}/progress`, progress),
  
  // Goal Objectives (Sub-objectives)
  addObjective: (ispId, goalId, objective) => API.post(`/clts/isp/${ispId}/goals/${goalId}/objectives`, objective),
  updateObjective: (ispId, goalId, objectiveId, data) => API.put(`/clts/isp/${ispId}/goals/${goalId}/objectives/${objectiveId}`, data),
  deleteObjective: (ispId, goalId, objectiveId) => API.delete(`/clts/isp/${ispId}/goals/${goalId}/objectives/${objectiveId}`),
  
  // Services
  addService: (ispId, service) => API.post(`/clts/isp/${ispId}/services`, service),
  updateService: (ispId, serviceId, data) => API.put(`/clts/isp/${ispId}/services/${serviceId}`, data),
  
  // Team Members
  addTeamMember: (ispId, member) => API.post(`/clts/isp/${ispId}/team`, member),
  removeTeamMember: (ispId, memberId) => API.delete(`/clts/isp/${ispId}/team/${memberId}`),
  
  // Signatures
  addSignature: (ispId, signature) => API.post(`/clts/isp/${ispId}/signatures`, signature),
  signISP: (ispId, signatureId, data) => API.put(`/clts/isp/${ispId}/signatures/${signatureId}/sign`, data),
  
  // Amendments
  createAmendment: (ispId, amendment) => API.post(`/clts/isp/${ispId}/amendments`, amendment),
  approveAmendment: (ispId, amendmentId) => API.put(`/clts/isp/${ispId}/amendments/${amendmentId}/approve`),
  
  // Documents
  addDocument: (ispId, document) => API.post(`/clts/isp/${ispId}/documents`, document),
  deleteDocument: (ispId, documentId) => API.delete(`/clts/isp/${ispId}/documents/${documentId}`),
  uploadDocument: (ispId, file, documentType, description) => {
    const formData = new FormData();
    formData.append('file', file);
    return API.post(`/clts/isp/${ispId}/documents/upload?document_type=${documentType}${description ? `&description=${encodeURIComponent(description)}` : ''}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadDocument: (ispId, documentId) => API.get(`/clts/isp/${ispId}/documents/${documentId}/download`, { responseType: 'blob' }),
  getDocumentDownloadUrl: (ispId, documentId) => `${API.defaults.baseURL}/clts/isp/${ispId}/documents/${documentId}/download`,
  
  // Timeline
  getTimeline: (ispId) => API.get(`/clts/isp/${ispId}/timeline`),
  
  // Participant Summary
  getParticipantISPSummary: (participantId) => API.get(`/clts/isp/participant/${participantId}/summary`),
  
  // Link visit to goals
  linkVisitToGoals: (ispId, visitId, goalIds) => API.post(`/clts/isp/${ispId}/link-visit/${visitId}`, goalIds),
  
  // Templates
  getTemplates: () => API.get('/clts/isp/templates'),
  createTemplate: (data) => API.post('/clts/isp/templates', data),
  updateTemplate: (id, data) => API.put(`/clts/isp/templates/${id}`, data),
  deleteTemplate: (id) => API.delete(`/clts/isp/templates/${id}`),
  createFromTemplate: (templateId, participantId, effectiveDate) => 
    API.post(`/clts/isp/from-template/${templateId}?participant_id=${participantId}&effective_date=${effectiveDate}`),
  
  // Signature Notifications
  sendSignatureReminder: (ispId, signatureId) => API.post(`/clts/isp/${ispId}/signatures/${signatureId}/send-reminder`),
  sendAllReminders: (ispId) => API.post(`/clts/isp/${ispId}/send-all-reminders`),
  
  // Auto-sync visits to ISP
  autoLinkVisit: (visitId) => API.post(`/clts/isp/auto-link-visit/${visitId}`),
  
  // Reports
  getReport: (ispId) => API.get(`/clts/isp/${ispId}/report`),
  getReportHtml: (ispId) => `${API.defaults.baseURL}/clts/isp/${ispId}/report/html`,
};

// Transportation API - Uber-style
export const transportationApi = {
  // Trips
  getTrips: (params) => API.get('/transportation/trips', { params }),
  getTodaysTrips: () => API.get('/transportation/trips/today'),
  getMyTrips: () => API.get('/transportation/trips/my-trips'),
  getActiveTrips: () => API.get('/transportation/trips/active'),
  getTrip: (id) => API.get(`/transportation/trips/${id}`),
  createTrip: (data) => API.post('/transportation/trips', data),
  updateTrip: (id, data) => API.put(`/transportation/trips/${id}`, data),
  deleteTrip: (id) => API.delete(`/transportation/trips/${id}`),
  getTrip: (id) => API.get(`/transportation/trips/${id}`),
  
  // Trip Actions (Uber-style)
  startTrip: (id) => API.post(`/transportation/trips/${id}/start`),
  arrivePickup: (id) => API.post(`/transportation/trips/${id}/arrive-pickup`),
  pickupParticipant: (id, data) => API.post(`/transportation/trips/${id}/pickup`, data),
  markNoShow: (id, participantId) => API.post(`/transportation/trips/${id}/no-show?participant_id=${participantId}`),
  startToDestination: (id) => API.post(`/transportation/trips/${id}/start-to-destination`),
  arriveDestination: (id) => API.post(`/transportation/trips/${id}/arrive-destination`),
  dropoffParticipant: (id, data) => API.post(`/transportation/trips/${id}/dropoff`, data),
  completeTrip: (id, miles) => API.post(`/transportation/trips/${id}/complete${miles ? `?total_miles=${miles}` : ''}`),
  cancelTrip: (id, reason) => API.post(`/transportation/trips/${id}/cancel${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`),
  
  // Driver Location
  updateLocation: (data) => API.post('/transportation/location', data),
  getAllDriverLocations: () => API.get('/transportation/location/drivers'),
  getDriverLocation: (driverId) => API.get(`/transportation/location/${driverId}`),
  getDriverLocationHistory: (driverId, params) => API.get(`/transportation/location/${driverId}/history`, { params }),
  
  // Vehicles
  getVehicles: (params) => API.get('/transportation/vehicles', { params }),
  getVehicle: (id) => API.get(`/transportation/vehicles/${id}`),
  createVehicle: (data) => API.post('/transportation/vehicles', data),
  updateVehicle: (id, data) => API.put(`/transportation/vehicles/${id}`, data),
  deleteVehicle: (id) => API.delete(`/transportation/vehicles/${id}`),
  
  // Drivers & Stats
  getDrivers: () => API.get('/transportation/drivers'),
  getStats: () => API.get('/transportation/stats'),
  
  // Route Optimization
  optimizeRoute: (id, startLat, startLng) => API.post(`/transportation/trips/${id}/optimize-route${startLat ? `?start_lat=${startLat}&start_lng=${startLng}` : ''}`),
  previewRoute: (id, startLat, startLng) => API.get(`/transportation/trips/${id}/route-preview${startLat ? `?start_lat=${startLat}&start_lng=${startLng}` : ''}`),
  
  // Multi-Stop Route Optimization
  optimizeMultiStopRoute: (stops, startAddress, returnToStart) => API.post('/transportation/route/optimize', stops, {
    params: { 
      start_address: startAddress || undefined, 
      return_to_start: returnToStart || false 
    }
  }),
  
  // Distance Calculation (Point-to-Point with Street-Level Geocoding)
  calculateDistance: (fromAddress, toAddress) => API.post(`/transportation/distance/calculate?from_address=${encodeURIComponent(fromAddress)}&to_address=${encodeURIComponent(toAddress)}`),
  
  // Mileage Calculation
  calculateMileage: (addresses, rate) => API.post(`/transportation/mileage/calculate?reimbursement_rate=${rate || 0.67}`, addresses),
  calculateTripMileage: (tripId) => API.post(`/transportation/trips/${tripId}/calculate-mileage`),
  getMileageReport: (driverId, startDate, endDate) => API.get(`/transportation/mileage/report?driver_id=${driverId}&start_date=${startDate}&end_date=${endDate}`),
  
  // DSP Assignments
  createDSPAssignment: (dspId, data) => API.post(`/transportation/dsp/${dspId}/assignments`, data),
  getDSPAssignments: (dspId, status) => API.get(`/transportation/dsp/${dspId}/assignments${status ? `?status=${status}` : ''}`),
  getDSPParticipants: (dspId) => API.get(`/transportation/dsp/${dspId}/participants`),
  updateDSPAssignment: (assignmentId, data) => API.put(`/transportation/dsp/assignments/${assignmentId}`, data),
  deleteDSPAssignment: (assignmentId) => API.delete(`/transportation/dsp/assignments/${assignmentId}`),
  
  // DSP Certifications
  getDSPCredentials: (dspId) => API.get(`/transportation/dsp/${dspId}/credentials`),
  updateDSPCredentials: (dspId, data) => API.put(`/transportation/dsp/${dspId}/credentials`, data),
  addDSPCertification: (dspId, data) => API.post(`/transportation/dsp/${dspId}/certifications`, data),
  getExpiringCredentials: (days) => API.get(`/transportation/dsp/expiring-credentials?days=${days || 30}`),
  
  // Visit Logging
  logVisitFromTrip: (tripId, data) => API.post(`/transportation/trips/${tripId}/log-visit`, data),
  getTripVisits: (tripId) => API.get(`/transportation/trips/${tripId}/visits`),
  getDSPVisitHistory: (dspId, params) => API.get(`/transportation/dsp/${dspId}/visit-history`, { params }),
  
  // DSP Visit Reports
  generateDSPVisitReport: (dspId, startDate, endDate, participantId) => 
    API.get(`/transportation/dsp/${dspId}/visit-report?start_date=${startDate}&end_date=${endDate}${participantId ? `&participant_id=${participantId}` : ''}`),
  getDSPMonthlySummary: (dspId, year, month) => 
    API.get(`/transportation/dsp/${dspId}/monthly-summary?year=${year}&month=${month}`),
  
  // Driver Credentials Upload
  uploadCredentialDocument: (dspId, formData) => 
    API.post(`/transportation/dsp/${dspId}/credentials/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  getCredentialDocuments: (dspId) => API.get(`/transportation/dsp/${dspId}/credentials/documents`),
  deleteCredentialDocument: (dspId, documentId) => API.delete(`/transportation/dsp/${dspId}/credentials/documents/${documentId}`),
  updateDrivingCredentials: (dspId, formData) => 
    API.put(`/transportation/dsp/${dspId}/driving-credentials`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  
  // Recurring Trip Templates
  getRecurringTemplates: () => API.get('/transportation/recurring-templates'),
  getRecurringTemplate: (id) => API.get(`/transportation/recurring-templates/${id}`),
  createRecurringTemplate: (data) => API.post('/transportation/recurring-templates', data),
  updateRecurringTemplate: (id, data) => API.put(`/transportation/recurring-templates/${id}`, data),
  deleteRecurringTemplate: (id) => API.delete(`/transportation/recurring-templates/${id}`),
  generateTripsFromTemplate: (id, weeksAhead = 2) => API.post(`/transportation/recurring-templates/${id}/generate?weeks_ahead=${weeksAhead}`),
};

// Attachments API (general file attachments for CLTS records)
export const attachmentsApi = {
  // Upload attachment to any record type
  upload: (file, recordType, recordId, category = 'general', description = '') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('record_type', recordType);
    formData.append('record_id', recordId);
    formData.append('category', category);
    formData.append('description', description);
    return API.post('/clts/attachments/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // List attachments for a record
  list: (recordType, recordId, category = null) => {
    const params = category ? `?category=${category}` : '';
    return API.get(`/clts/attachments/${recordType}/${recordId}${params}`);
  },
  
  // Get download URL for an attachment
  getDownloadUrl: (attachmentId) => `${API.defaults.baseURL}/clts/attachments/download/${attachmentId}`,
  
  // Download an attachment
  download: (attachmentId) => API.get(`/clts/attachments/download/${attachmentId}`, { responseType: 'blob' }),
  
  // Delete an attachment
  delete: (attachmentId) => API.delete(`/clts/attachments/${attachmentId}`),
};

export default API;
